/**
 * The fixed headers of the messages Studio sends DANI about crosstabs, so the chat can leave them
 * out once DANI has saved them as history (M4S-5777 X149–X151, docs/pdlc/crosstabs-dani.md).
 *
 * The pattern is feasibility's (`src/lib/feasibility/dani/primer-marks.ts`): the rules message
 * starts with a fixed header, DANI's acknowledgement is a fixed reply, and every other message
 * Studio sends (a launch, the engine's figures) starts with its own header. Saved history does not
 * keep "hidden", so a reopened conversation would otherwise show them as the person's messages.
 *
 * Pure.
 */

/** The first line of every crosstab rules message (`crosstabPrimer`). */
export const CROSSTAB_PRIMER_HEADER = 'CROSSTAB RULES FOR DANI (not shown to the person)';

/** DANI's fixed acknowledgement of the rules. */
export const CROSSTAB_PRIMER_ACK =
    'Understood. I will agree a crosstab plan with you using only the study’s own surveys and questions, state my assumptions, never compute or restate a number myself, and write the crosstab-plan block only when you agree.';

/** How the message starts when someone presses Build a crosstab in a DANI chat. */
export const CROSSTAB_LAUNCH_HEADER = 'CROSSTAB REQUEST FROM STUDIO (not from the person)';

/**
 * How the engine's figures start when Studio hands them to DANI (decision L5: once per Run, on the
 * next send). DANI may quote them; it never restates them as its own or recomputes them.
 */
export const CROSSTAB_FIGURES_HEADER = 'CROSSTAB FIGURES FROM STUDIO (the engine’s, last Run)';

/** The fenced block language DANI writes a crosstab plan in, and the chat draws as a card. */
export const CROSSTAB_PLAN_LANGUAGE = 'crosstab-plan';

function textOf(content: unknown): string {
    if (typeof content === 'string') return content;
    if (!Array.isArray(content)) return '';
    return content
        .map((b) =>
            b && typeof b === 'object' && (b as { type?: unknown }).type === 'text'
                ? String((b as { text?: unknown }).text ?? '')
                : '',
        )
        .join('\n');
}

type MessageLike = { role?: unknown; content: unknown };

/** True for the rules message or DANI's acknowledgement of them. */
export function isCrosstabPrimer(message: MessageLike): boolean {
    const text = textOf(message.content).trim();
    if (message.role === 'user') return text.startsWith(CROSSTAB_PRIMER_HEADER);
    if (message.role === 'assistant') return text === CROSSTAB_PRIMER_ACK;
    return false;
}

/** True for a launch or the engine's figures sent by Studio. */
export function isCrosstabStudioMessage(message: MessageLike): boolean {
    if (message.role !== 'user') return false;
    const text = textOf(message.content).trim();
    return text.startsWith(CROSSTAB_LAUNCH_HEADER) || text.startsWith(CROSSTAB_FIGURES_HEADER);
}

/** Messages the chat never shows: the rules, their acknowledgement, launches and figures. */
export function isHiddenCrosstabMessage(message: MessageLike): boolean {
    return isCrosstabPrimer(message) || isCrosstabStudioMessage(message);
}

/** True when a conversation already carries DANI's crosstab rules (sent once per conversation). */
export function hasCrosstabRules(messages: readonly MessageLike[]): boolean {
    return messages.some(
        (m) => m.role === 'user' && textOf(m.content).includes(CROSSTAB_PRIMER_HEADER),
    );
}

/**
 * What Studio asks DANI when someone presses Build a crosstab in a chat: agree a crosstab plan from
 * the conversation, ask only what changes the table, state the assumptions, then write the block.
 * `rules` is the primer's text when the conversation does not have it yet.
 */
export function crosstabLaunchPrompt(rules: string | null, context?: string | null): string {
    return [
        CROSSTAB_LAUNCH_HEADER,
        'The person pressed Build a crosstab. They want a crosstab of the study this conversation is about.',
        'Work the plan out from everything above: which surveys (by survey ID), which questions go in the rows and which in the banner, any nets or variables, filters, weighting and the statistics settings.',
        'Start with one or two sentences on what you understood. If the study or its surveys are not clear, ask, at most two questions. Otherwise propose the plan, list your assumptions (banner, confidence, weighting), and write the crosstab-plan block once the person agrees.',
        ...(context ? ['', context] : []),
        ...(rules ? ['', rules] : []),
    ].join('\n');
}
